import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import confetti from "canvas-confetti";
import { PartyPopper, Share2, Copy, Download, X } from "lucide-react";
import { fmt, fmtDate } from "@/lib/constants";
import { toast } from "sonner";

const fireConfetti = () => {
  const end = Date.now() + 1800;
  const colors = ["#14B8A6", "#A78BFA", "#22D3EE", "#F59E0B", "#34D399"];
  (function frame() {
    confetti({ particleCount: 5, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors });
    confetti({ particleCount: 5, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  confetti({ particleCount: 160, spread: 100, origin: { y: 0.55 }, colors, scalar: 1.1 });
};

const monthsBetween = (a, b) => Math.max(1, Math.round((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24 * 30.44)));

export default function PayoffCelebration({ payment, onClose }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const m = payment?.milestone;

  useEffect(() => { if (payment) fireConfetti(); }, [payment]);
  if (!payment || !m) return null;

  const months = monthsBetween(m.first_paid_at, m.paid_off_at);
  const text = `I just paid off ${payment.debt_name} — ${fmt(m.starting_balance)} gone in ${months} month${months === 1 ? "" : "s"}! ${fmt(m.freed_monthly)}/mo freed up for the next debt. #DebtFree #OWEverit`;

  const copy = async () => { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Debt paid off!", text }); } catch {} }
    else copy();
  };
  const download = () => {
    setBusy(true);
    const c = document.createElement("canvas"); c.width = 1200; c.height = 630;
    const ctx = c.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, 1200, 630); g.addColorStop(0, "#0B1220"); g.addColorStop(1, "#1E1B4B");
    ctx.fillStyle = g; ctx.fillRect(0, 0, 1200, 630);
    ctx.fillStyle = "#14B8A6"; ctx.fillRect(0, 0, 1200, 10);
    ctx.fillStyle = "#5EEAD4"; ctx.font = "bold 26px sans-serif"; ctx.fillText("MILESTONE · DEBT PAID OFF", 80, 110);
    ctx.fillStyle = "#FFFFFF"; ctx.font = "bold 76px sans-serif"; ctx.fillText(payment.debt_name, 80, 210);
    ctx.font = "34px sans-serif"; ctx.fillStyle = "#CBD5E1";
    ctx.fillText(`${fmt(m.starting_balance)} cleared in ${months} month${months === 1 ? "" : "s"} · ${m.payments_count} payments`, 80, 280);
    ctx.fillText(`${fmt(m.total_interest)} interest paid at ${m.interest_rate}% APR`, 80, 335);
    ctx.fillStyle = "#34D399"; ctx.font = "bold 40px sans-serif"; ctx.fillText(`+${fmt(m.freed_monthly)}/mo freed for the next debt`, 80, 430);
    ctx.fillStyle = "#94A3B8"; ctx.font = "26px sans-serif"; ctx.fillText(`${fmtDate(m.paid_off_at)} · OWEverit`, 80, 560);
    const a = document.createElement("a"); a.href = c.toDataURL("image/png"); a.download = `paid-off-${payment.debt_name.replace(/\s+/g, "-").toLowerCase()}.png`; a.click();
    setBusy(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="payoff-celebration">
      <div onClick={e => e.stopPropagation()} className="w-full max-w-lg fade-up">
        <div ref={cardRef} className="relative rounded-3xl p-7 text-white overflow-hidden shadow-2xl border border-teal-400/30"
          style={{ background: "linear-gradient(135deg,#0B1220 0%,#1E1B4B 100%)" }} data-testid="payoff-card">
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-teal-500/30 blur-3xl" />
          <div className="absolute -bottom-16 -left-10 h-40 w-40 rounded-full bg-violet-500/30 blur-3xl" />
          <button onClick={onClose} className="absolute right-4 top-4 p-1 rounded-lg hover:bg-white/10" data-testid="celebration-close"><X className="h-4 w-4" /></button>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-teal-300 font-bold"><PartyPopper className="h-4 w-4" /> Milestone · Debt paid off</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-3 leading-tight" data-testid="celebration-debt-name">{payment.debt_name}</h2>
          <p className="text-slate-300 text-sm mt-2">{fmt(m.starting_balance)} cleared in {months} month{months === 1 ? "" : "s"} · {m.payments_count} payment{m.payments_count === 1 ? "" : "s"}</p>
          <div className="grid grid-cols-3 gap-3 mt-6">
            <Stat label="Total paid" value={fmt(m.total_paid)} />
            <Stat label="Interest paid" value={fmt(m.total_interest)} tone="text-rose-300" />
            <Stat label="Freed / month" value={`+${fmt(m.freed_monthly)}`} tone="text-emerald-300" />
          </div>
          <div className="text-[11px] text-slate-400 mt-5">{fmtDate(m.paid_off_at)} · That {fmt(m.freed_monthly)} now rolls into your snowball.</div>
        </div>
        <div className="flex items-center justify-center gap-2 mt-4">
          <Btn onClick={share} icon={Share2} testid="celebration-share">Share</Btn>
          <Btn onClick={copy} icon={Copy} testid="celebration-copy">Copy text</Btn>
          <Btn onClick={download} icon={Download} testid="celebration-download" disabled={busy}>Download card</Btn>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Stat({ label, value, tone = "" }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className={`font-mono font-bold text-lg mt-1 ${tone}`}>{value}</div>
    </div>
  );
}
function Btn({ onClick, icon: Icon, children, testid, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium hover:bg-accent transition disabled:opacity-50">
      <Icon className="h-4 w-4" /> {children}
    </button>
  );
}
